import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import { Brand } from '@/app/layout/Brand';
import { Breadcrumbs } from '@/app/layout/Breadcrumbs';
import { MobileDrawer } from '@/app/layout/MobileDrawer';
import { AccountMenu } from '@/features/auth/AccountMenu';

/**
 * The one shell every area of the dashboard shares — the normal app, Beheer,
 * Profiel, Instellingen (§ Basis layout): a full-height left aside (logo,
 * a top slot right under it — the company switcher in the normal app, a
 * "Terug naar de app" link everywhere else — the caller's nav, and the
 * account menu pinned at the bottom) next to the content column. Below
 * `lg` the aside collapses into a hamburger-triggered drawer holding the
 * exact same pieces, via `MobileDrawer`'s render-prop `close`. No area gets
 * a second, differently-shaped nav next to this one — that was the bug
 * this component fixes (Beheer/Profiel/Instellingen each had their own
 * local two-column grid, mounted *inside* the normal app's own sidebar).
 */
export function AppShell({
  topSlot,
  renderNav,
  beforeContent,
}: {
  /** Rendered directly under `Brand`, above the nav. */
  topSlot: ReactNode;
  /** Render prop (not a plain node) so the mobile drawer's copy can wire
   * `onNavigate` to close itself on a nav click while the desktop aside's
   * copy gets none. */
  renderNav: (onNavigate?: () => void) => ReactNode;
  /** Extra content above the breadcrumbs — `ConfigWarning`, main app only. */
  beforeContent?: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <div className="bg-paper flex min-h-screen">
      <a
        href="#main-content"
        className="bg-mint text-ink focus:rounded-pill sr-only px-4 py-2 focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50"
      >
        {t('shell.skipToContent')}
      </a>

      <aside className="border-line bg-paper surface-glow sticky top-0 hidden h-screen w-64 shrink-0 flex-col gap-6 border-r px-4 py-6 lg:flex">
        <Brand />
        {topSlot}
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto">{renderNav()}</div>
          <div className="mt-4 shrink-0">
            <AccountMenu variant="row" />
          </div>
        </div>
      </aside>

      <div className="min-w-0 flex-1">
        <header
          role="banner"
          className="border-line bg-paper sticky top-0 z-20 flex h-14 items-center gap-4 border-b px-4 lg:px-8"
        >
          <MobileDrawer>
            {(close) => (
              <>
                <Brand />
                {topSlot}
                <div className="flex min-h-0 flex-1 flex-col">
                  <div className="flex-1 overflow-y-auto">{renderNav(close)}</div>
                  <div className="mt-4 shrink-0">
                    <AccountMenu variant="row" />
                  </div>
                </div>
              </>
            )}
          </MobileDrawer>
        </header>

        <main id="main-content" className="mx-auto w-full max-w-[80rem] p-4 md:p-6 lg:p-8">
          {beforeContent}
          <Breadcrumbs />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
